import type { components } from "@repo/api-types/merchant"
import type {
  AutoRevokePreview,
  OrderPrepaymentAutoRevokeOperation,
  OrderPrepaymentFlow,
  OrderPrepaymentPaymentMethod,
  OrderPrepaymentPort,
  OrderPrepaymentReceipt,
  OrderPrepaymentSummary,
  PrepaymentPaymentBreakdown,
} from "./types/orderPrepayment"
import { ElMessageBox } from "element-plus"
import { normalizeOpaqueLongId } from "@/utils/opaqueLongId"
import { request } from "@/utils/service"

const PREFIX = "teashop_merchant/merchant_order_prepayment"

type PrepaymentBreakdownDto = components["schemas"]["OrderPrepaymentPaymentBreakdown"]
type PrepaymentSummaryDto = components["schemas"]["OrderPrepaymentSummaryResponse"] & { tableRecordId?: number }
type PrepaymentSummaryApiResponse = components["schemas"]["ROrderPrepaymentSummaryResponse"]
type PrepaymentReceiveRequestDto = components["schemas"]["OrderPrepaymentReceiveRequest"]
type PrepaymentRefundRequestDto = components["schemas"]["OrderPrepaymentRefundRequest"]
type PrepaymentAutoRefundRequestDto = components["schemas"]["OrderPrepaymentAutoRefundRequest"]

interface PendingFinancialRequest {
  requestId: string
}

const pendingMemory = new Map<string, PendingFinancialRequest>()
const summaryRequests = new Map<string, Promise<OrderPrepaymentSummary>>()
const summaryListeners = new Map<string, Set<(summary: OrderPrepaymentSummary) => void>>()
const summaryMutationEpochs = new Map<string, number>()
const latestSummaries = new Map<string, OrderPrepaymentSummary>()

function pendingStorageKey(action: string, identity: Array<string | number | undefined>) {
  return `tea-shop:order-prepayment:${action}:${identity.map(value => String(value ?? "")).join(":")}`
}

function readPending(key: string, action: string, tableRecordId: string): PendingFinancialRequest {
  const storage = typeof sessionStorage === "undefined" ? undefined : sessionStorage
  const serialized = storage?.getItem(key)
  if (serialized) {
    try {
      return JSON.parse(serialized) as PendingFinancialRequest
    } catch {
      storage?.removeItem(key)
    }
  }
  const existing = pendingMemory.get(key)
  if (existing) return existing
  const created = { requestId: idempotencyKey(action, tableRecordId) }
  pendingMemory.set(key, created)
  storage?.setItem(key, JSON.stringify(created))
  return created
}

function clearPending(key: string) {
  pendingMemory.delete(key)
  if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(key)
}

function paymentMethod(paymentType?: number, diyPaymentType?: number, name = "未知"): OrderPrepaymentPaymentMethod {
  const type = paymentType === 1
    ? "CASH"
    : paymentType === 3
      ? "WECHAT"
      : paymentType === 4
        ? "ALIPAY"
        : paymentType === 5
          ? "BANK_CARD"
          : "CUSTOM"
  return { id: String(paymentType === 7 ? diyPaymentType ?? 0 : paymentType ?? 0), name, type }
}

function paymentType(method: OrderPrepaymentPaymentMethod) {
  return ({ CASH: 1, WECHAT: 3, ALIPAY: 4, BANK_CARD: 5, CUSTOM: 7 } as const)[method.type]
}

function flowType(value?: string): OrderPrepaymentFlow["type"] {
  const allowed: OrderPrepaymentFlow["type"][] = [
    "RECEIPT",
    "REFUND",
    "AUTO_REVOKE",
    "SETTLEMENT_CONSUME",
    "REVERSE_RESTORE",
  ]
  return allowed.includes(value as OrderPrepaymentFlow["type"])
    ? value as OrderPrepaymentFlow["type"]
    : "RECEIPT"
}

function flowStatus(value?: string): OrderPrepaymentFlow["status"] {
  return value === "PROCESSING" || value === "FAILED" ? value : "SUCCESS"
}

function mapBreakdown(value: PrepaymentBreakdownDto): PrepaymentPaymentBreakdown {
  const method = paymentMethod(value.sourcePaymentType, value.sourcePaymentTypeId, value.sourcePaymentTypeName)
  return {
    sourcePaymentType: method.type,
    sourcePaymentTypeId: method.id,
    sourcePaymentTypeName: method.name,
    amount: value.amount ?? 0,
    receiptCount: value.receiptCount ?? 0,
    displayName: value.displayName ?? `预付款（${method.name}）`,
  }
}

function mapSummary(value: PrepaymentSummaryDto): OrderPrepaymentSummary {
  const receipts: OrderPrepaymentReceipt[] = (value.receipts ?? []).map(item => ({
    id: String(item.id ?? ""),
    amount: item.amount ?? 0,
    refundedAmount: item.refundedAmount ?? 0,
    occupiedAmount: item.occupiedAmount ?? 0,
    availableAmount: item.availableAmount ?? 0,
    paymentMethod: paymentMethod(item.paymentType, item.diyPaymentType, item.paymentName),
    receivedAt: item.receivedAt ?? "",
  }))
  const flows: OrderPrepaymentFlow[] = (value.flows ?? []).map(item => ({
    id: String(item.id ?? ""),
    receiptId: String(item.receiptId ?? ""),
    type: flowType(item.type),
    status: flowStatus(item.status),
    amount: item.amount ?? 0,
    paymentMethod: paymentMethod(
      item.paymentType ?? item.originalPaymentType,
      item.diyPaymentType ?? item.originalDiyPaymentType,
      item.paymentName ?? item.originalPaymentName,
    ),
    occurredAt: item.occurredAt ?? "",
    description: item.failureReason || item.reason || "",
  }))
  return {
    tableRecordId: String(value.tableRecordId ?? ""),
    orderId: String(value.orderId ?? ""),
    orderAmount: value.orderAmount ?? 0,
    receivedAmount: value.receivedAmount ?? 0,
    refundedAmount: value.refundedAmount ?? 0,
    occupiedAmount: value.occupiedAmount ?? 0,
    availableAmount: value.availableAmount ?? 0,
    overpaidAmount: value.overpaidAmount ?? 0,
    version: value.version ?? "0",
    prototype: value.prototype ?? false,
    canReceive: value.canReceive ?? false,
    canRefund: value.canRefund ?? false,
    hasPendingOrFailedRefund: value.hasPendingOrFailedRefund ?? false,
    receipts,
    flows,
    paymentBreakdown: (value.paymentBreakdown ?? []).map(mapBreakdown),
    settlementBreakdown: (value.settlementBreakdown ?? []).map(mapBreakdown),
  }
}

function unwrap(response: PrepaymentSummaryApiResponse) {
  if (response.code !== 0 || !response.data) throw new Error(response.msg || "预付款请求失败")
  return mapSummary(response.data)
}

function idempotencyKey(action: string, tableRecordId: string) {
  const uuid = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `prepayment:${action}:table-record:${tableRecordId}:${uuid}`
}

function requireTableRecordId(tableRecordId: string) {
  if (!/^\d+$/.test(tableRecordId)) {
    throw new Error("预付款资金操作缺少桌台消费记录ID，请刷新桌台后重试")
  }
  return tableRecordId
}

function withOrderAmount(summary: OrderPrepaymentSummary, orderAmount?: number) {
  if (orderAmount == null || !Number.isFinite(orderAmount)) return summary
  const normalizedAmount = Math.max(Math.round(orderAmount), 0)
  return {
    ...summary,
    orderAmount: normalizedAmount,
    overpaidAmount: Math.max(summary.availableAmount - normalizedAmount, 0),
  }
}

function subscribeSummary(tableRecordId: string, listener: (summary: OrderPrepaymentSummary) => void, orderAmount?: number) {
  const normalizedTableRecordId = requireTableRecordId(tableRecordId)
  const listeners = summaryListeners.get(normalizedTableRecordId) ?? new Set()
  const displayListener = (summary: OrderPrepaymentSummary) => listener(withOrderAmount(summary, orderAmount))
  listeners.add(displayListener)
  summaryListeners.set(normalizedTableRecordId, listeners)
  return () => {
    listeners.delete(displayListener)
    if (listeners.size === 0) summaryListeners.delete(normalizedTableRecordId)
  }
}

function publishSummary(tableRecordId: string, summary: OrderPrepaymentSummary) {
  latestSummaries.set(tableRecordId, summary)
  summaryListeners.get(tableRecordId)?.forEach(listener => listener(summary))
  return summary
}

function beginSummaryMutation(tableRecordId: string) {
  const epoch = (summaryMutationEpochs.get(tableRecordId) ?? 0) + 1
  summaryMutationEpochs.set(tableRecordId, epoch)
  summaryRequests.delete(tableRecordId)
  return epoch
}

function publishMutationSummary(tableRecordId: string, summary: OrderPrepaymentSummary, epoch: number) {
  if (summaryMutationEpochs.get(tableRecordId) !== epoch) {
    return latestSummaries.get(tableRecordId) ?? summary
  }
  return publishSummary(tableRecordId, summary)
}

function getSummaryRequest(tableRecordId: string, orderAmount?: number) {
  const normalizedTableRecordId = requireTableRecordId(tableRecordId)
  const existing = summaryRequests.get(normalizedTableRecordId)
  if (existing) return existing.then(summary => withOrderAmount(summary, orderAmount))
  const mutationEpoch = summaryMutationEpochs.get(normalizedTableRecordId) ?? 0
  const pending = request<PrepaymentSummaryApiResponse>({
    url: `${PREFIX}/table-records/${normalizedTableRecordId}/summary`,
    method: "GET",
  })
    .then(unwrap)
    .then(summary => {
      if ((summaryMutationEpochs.get(normalizedTableRecordId) ?? 0) !== mutationEpoch) {
        return latestSummaries.get(normalizedTableRecordId) ?? summary
      }
      return publishSummary(normalizedTableRecordId, summary)
    })
    .finally(() => {
      if (summaryRequests.get(normalizedTableRecordId) === pending) {
        summaryRequests.delete(normalizedTableRecordId)
      }
    })
  summaryRequests.set(normalizedTableRecordId, pending)
  return pending.then(summary => withOrderAmount(summary, orderAmount))
}

const formalPort: OrderPrepaymentPort = {
  getSummary: getSummaryRequest,
  subscribe: subscribeSummary,
  async receive(input) {
    const tableRecordId = requireTableRecordId(input.tableRecordId)
    const mutationEpoch = beginSummaryMutation(tableRecordId)
    const type = paymentType(input.paymentMethod)
    const pendingKey = pendingStorageKey("receive", [
      tableRecordId,
      input.amount,
      type,
      input.paymentMethod.id,
    ])
    const pending = readPending(pendingKey, "receive", tableRecordId)
    const data: PrepaymentReceiveRequestDto = {
      amount: input.amount,
      paymentType: type,
      diyPaymentType: type === 7 ? Number(input.paymentMethod.id) : undefined,
      paymentName: input.paymentMethod.name,
      manualConfirmed: input.manualConfirmed,
    }
    const response = await request<PrepaymentSummaryApiResponse>({
      url: `${PREFIX}/table-records/${tableRecordId}/receipts`,
      method: "POST",
      data,
      customHeaders: { "Idempotency-Key": pending.requestId },
    })
    const summary = unwrap(response)
    clearPending(pendingKey)
    return withOrderAmount(publishMutationSummary(tableRecordId, summary, mutationEpoch), input.orderAmount)
  },
  async refund(input) {
    const tableRecordId = requireTableRecordId(input.tableRecordId)
    const mutationEpoch = beginSummaryMutation(tableRecordId)
    const type = paymentType(input.paymentMethod)
    const pendingKey = pendingStorageKey("refund", [
      tableRecordId,
      input.receiptId,
      input.amount,
      type,
      input.paymentMethod.id,
    ])
    const pending = readPending(pendingKey, "refund", tableRecordId)
    const data: PrepaymentRefundRequestDto = {
      amount: input.amount,
      refundPaymentType: type,
      refundDiyPaymentType: type === 7 ? Number(input.paymentMethod.id) : undefined,
      refundPaymentName: input.paymentMethod.name,
      reason: input.reason,
      manualConfirmed: input.manualConfirmed,
    }
    const response = await request<PrepaymentSummaryApiResponse>({
      url: `${PREFIX}/table-records/${tableRecordId}/receipts/${input.receiptId}/refunds`,
      method: "POST",
      data,
      sensitiveApprovalBusinessName: "预付款退款",
      sensitiveApprovalReason: input.reason,
      customHeaders: { "Idempotency-Key": pending.requestId },
    })
    const summary = unwrap(response)
    clearPending(pendingKey)
    return withOrderAmount(publishMutationSummary(tableRecordId, summary, mutationEpoch), input.orderAmount)
  },
  async refreshRefund(tableRecordId, flowId, orderAmount) {
    const normalizedTableRecordId = requireTableRecordId(tableRecordId)
    const mutationEpoch = beginSummaryMutation(normalizedTableRecordId)
    const response = await request<PrepaymentSummaryApiResponse>({
      url: `${PREFIX}/table-records/${normalizedTableRecordId}/refunds/${flowId}/refresh`,
      method: "POST",
    })
    const summary = unwrap(response)
    return withOrderAmount(publishMutationSummary(normalizedTableRecordId, summary, mutationEpoch), orderAmount)
  },
  async previewAutoRevoke(tableRecordId, operation): Promise<AutoRevokePreview> {
    const summary = await getSummaryRequest(tableRecordId)
    return {
      operation,
      totalAmount: summary.availableAmount,
      warning: "系统不会自动退款，请先按清单完成实际退款，确认全部退还后再继续原操作",
      breakdown: summary.paymentBreakdown,
      prototype: false,
    }
  },
  async autoRefund(tableRecordId, operation, manualConfirmed) {
    const normalizedTableRecordId = requireTableRecordId(tableRecordId)
    const mutationEpoch = beginSummaryMutation(normalizedTableRecordId)
    const pendingKey = pendingStorageKey("auto-refund", [normalizedTableRecordId, operation])
    const pending = readPending(pendingKey, `auto-${operation}`, normalizedTableRecordId)
    const data: PrepaymentAutoRefundRequestDto = { operation, manualConfirmed }
    const response = await request<PrepaymentSummaryApiResponse>({
      url: `${PREFIX}/table-records/${normalizedTableRecordId}/auto-refunds`,
      method: "POST",
      data,
      sensitiveApprovalBusinessName: "预付款批量退款",
      sensitiveApprovalReason: "订单关键操作前人工退清预付款",
      customHeaders: { "Idempotency-Key": pending.requestId },
    })
    const summary = unwrap(response)
    clearPending(pendingKey)
    return publishMutationSummary(normalizedTableRecordId, summary, mutationEpoch)
  },
  resetPrototype: getSummaryRequest,
}

export function getOrderPrepaymentPort(): OrderPrepaymentPort {
  return formalPort
}

export async function refreshProcessingOrderPrepaymentRefunds(summary: OrderPrepaymentSummary) {
  let current = summary
  const processingFlowIds = summary.flows
    .filter(flow => flow.status === "PROCESSING" && (flow.type === "REFUND" || flow.type === "AUTO_REVOKE"))
    .map(flow => flow.id)
  for (const flowId of processingFlowIds) {
    current = await formalPort.refreshRefund(current.tableRecordId, flowId, current.orderAmount)
  }
  return current
}

async function waitForRefundTerminal(summary: OrderPrepaymentSummary, maxAttempts = 5) {
  let current = summary
  for (let attempt = 0; attempt < maxAttempts && current.hasPendingOrFailedRefund; attempt += 1) {
    current = await refreshProcessingOrderPrepaymentRefunds(current)
    if (!current.flows.some(flow => flow.status === "PROCESSING")) break
    await new Promise(resolve => globalThis.setTimeout(resolve, 1000))
  }
  return current
}

const operationNames: Record<OrderPrepaymentAutoRevokeOperation, string> = {
  TRANSFER: "转台",
  ORDER_CANCEL: "整单取消",
  MERGE: "并台",
}

function formatYuan(amount: number) {
  return (amount / 100).toFixed(2)
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, character => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;",
  })[character] ?? character)
}

async function confirmManualRefundCompleted(
  summaries: OrderPrepaymentSummary[],
  operation: OrderPrepaymentAutoRevokeOperation,
) {
  const totalAmount = summaries.reduce((total, summary) => total + summary.availableAmount, 0)
  const grouped = new Map<string, { amount: number, name: string, receiptCount: number }>()
  summaries.flatMap(summary => summary.paymentBreakdown).forEach((item) => {
    if (item.amount <= 0) return
    const key = `${item.sourcePaymentType}:${item.sourcePaymentTypeId}:${item.sourcePaymentTypeName}`
    const existing = grouped.get(key) ?? { amount: 0, name: item.sourcePaymentTypeName || "未知方式", receiptCount: 0 }
    existing.amount += item.amount
    existing.receiptCount += item.receiptCount
    grouped.set(key, existing)
  })
  const breakdown = [...grouped.values()]
    .map(item => `<div style="display:flex;justify-content:space-between;gap:16px;padding:6px 0;">
      <span>${escapeHtml(item.name)}${item.receiptCount > 1 ? `（${item.receiptCount}笔）` : ""}</span>
      <strong>￥${formatYuan(item.amount)}</strong>
    </div>`)
    .join("")
  try {
    await ElMessageBox.confirm(
      `<div style="line-height:1.7;">
        <p style="margin:0 0 8px;">当前操作涉及预付款 <strong>￥${formatYuan(totalAmount)}</strong>。系统不会自动退款。</p>
        <div style="margin:8px 0;padding:8px 12px;background:#f7f8fa;border-radius:6px;">${breakdown}</div>
        <p style="margin:8px 0 0;">请先按上述方式向顾客完成实际退款；确认后系统才会登记退款并继续${operationNames[operation]}。</p>
      </div>`,
      "确认已完成预付款退款",
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: "确认已全部实际退款",
        cancelButtonText: "取消",
        type: "warning",
        closeOnClickModal: false,
        closeOnPressEscape: false,
      },
    )
  } catch {
    throw new Error(`已取消${operationNames[operation]}`)
  }
}

export async function refundPrepaymentsBeforeTableOperation(
  tableRecordIds: Array<number | string>,
  operation: OrderPrepaymentAutoRevokeOperation,
) {
  const normalizedTableRecordIds = [...new Set(tableRecordIds
    .map(value => normalizeOpaqueLongId(value))
    .filter((value): value is string => Boolean(value)))]
  if (normalizedTableRecordIds.length === 0) return
  const summaries = await Promise.all(normalizedTableRecordIds.map(async (tableRecordId) => {
    let summary = await formalPort.getSummary(tableRecordId)
    if (summary.hasPendingOrFailedRefund) summary = await waitForRefundTerminal(summary, 2)
    return summary
  }))
  const activeSummaries = summaries.filter(summary =>
    summary.availableAmount > 0 || summary.hasPendingOrFailedRefund)
  if (activeSummaries.length === 0) return
  const refundableSummaries = activeSummaries.filter(summary => summary.availableAmount > 0)
  if (refundableSummaries.length > 0) await confirmManualRefundCompleted(refundableSummaries, operation)
  for (const summary of activeSummaries) {
    let latest: OrderPrepaymentSummary
    try {
      latest = await formalPort.autoRefund(summary.tableRecordId, operation, summary.availableAmount > 0)
    } catch (error) {
      const message = error instanceof Error ? error.message : ""
      if (message.includes("人工确认") || message.includes("未关联渠道支付记录")) {
        throw new Error("请先确认已向顾客实际退还全部预付款")
      }
      throw error
    }
    latest = await waitForRefundTerminal(latest)
    if (latest.availableAmount > 0 || latest.hasPendingOrFailedRefund) {
      throw new Error("预付款尚未全部退清，请核对实际退款后重试")
    }
  }
}

export async function refundPrepaymentBeforeTableOperation(
  tableRecordId: number,
  operation: OrderPrepaymentAutoRevokeOperation,
) {
  return refundPrepaymentsBeforeTableOperation([tableRecordId], operation)
}
